'use client';

import React from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { Cookie } from 'lucide-react';

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-8">
            <Cookie className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">Cookie Policy</h1>
              <p className="text-slate-500 text-sm">Last updated: 1 April 2026</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">What Are Cookies?</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                Cookies are small text files stored on your device when you visit a website. They help us provide a better experience by remembering your preferences and understanding how you use the platform.
              </p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-4">Cookies We Use</h2>
              <div className="space-y-4">
                {[
                  {
                    name: "Essential Cookies",
                    badge: "Always Active",
                    badgeColor: "bg-emerald-500/15 text-emerald-400",
                    description: "Required for the platform to function. These include authentication tokens (keeping you logged in), session cookies, and security cookies. You cannot opt out of these.",
                    examples: "supabase-auth-token, sb-access-token"
                  },
                  {
                    name: "Functional Cookies",
                    badge: "Optional",
                    badgeColor: "bg-amber-500/15 text-amber-400",
                    description: "Remember your preferences such as search filters, cookie consent choices, and UI settings.",
                    examples: "dm_cookie_consent, dm_preferences"
                  },
                  {
                    name: "Analytics Cookies",
                    badge: "Optional",
                    badgeColor: "bg-blue-500/15 text-blue-400",
                    description: "Help us understand how visitors use the platform so we can improve it. Data is anonymised and aggregated.",
                    examples: "Google Analytics (_ga, _gid)"
                  }
                ]?.map((cookie) => (
                  <div key={cookie?.name} className="bg-[hsl(222,35%,12%)] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-white">{cookie?.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-md ${cookie?.badgeColor}`}>{cookie?.badge}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed mb-2">{cookie?.description}</p>
                    <p className="text-xs text-slate-600">Examples: {cookie?.examples}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">Managing Cookies</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">
                You can manage your cookie preferences at any time using the cookie banner at the bottom of the page. You can also control cookies through your browser settings:
              </p>
              <ul className="text-slate-400 text-sm space-y-1.5 list-disc list-inside">
                <li>Chrome: Settings → Privacy and Security → Cookies</li>
                <li>Firefox: Options → Privacy & Security → Cookies</li>
                <li>Safari: Preferences → Privacy → Cookies</li>
                <li>Edge: Settings → Cookies and site permissions</li>
              </ul>
              <p className="text-slate-500 text-xs mt-3">Note: Disabling essential cookies will prevent you from logging in or using the marketplace.</p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">Contact</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                For questions about our use of cookies, contact us at <a href="mailto:privacy@drivenmarket.co.uk" className="text-amber-400 hover:text-amber-300">privacy@drivenmarket.co.uk</a>.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
